"use client";

import { useEffect, useId, useRef } from "react";

/**
 * A partner's official player, for videos that only play there (LiveJasmin promo videos through A.W.
 * Empire): their script finds this box by its data-awe-container-id and fills it with their own player
 * iframe. The script URL is used exactly as the partner gave it, with {CONTAINER} set to the box's id.
 * Nothing is downloaded or played by us. A view counts once the player has been open for a few seconds.
 */
export function PartnerPlayer({ videoId, embedUrl, poster, title }: { videoId: string; embedUrl: string; poster?: string | null; title: string }) {
  const box = useRef<HTMLDivElement>(null);
  const container = `awe-${useId().replace(/[^A-Za-z0-9_-]/g, "")}`;

  useEffect(() => {
    const el = box.current;
    if (!el) return;
    // Their script takes the mark off once it has filled the box, so a re-run sets it again.
    el.replaceChildren();
    el.setAttribute("data-awe-container-id", container);
    const script = document.createElement("script");
    script.src = embedUrl.replaceAll("{CONTAINER}", encodeURIComponent(container));
    script.async = true;
    document.body.appendChild(script);
    const viewed = window.setTimeout(() => navigator.sendBeacon?.(`/api/videos/${videoId}/view`), 5000);
    return () => {
      window.clearTimeout(viewed);
      script.remove();
      el.replaceChildren();
    };
  }, [embedUrl, container, videoId]);

  return (
    <div
      ref={box}
      aria-label={title}
      className="relative aspect-video w-full overflow-hidden bg-black bg-cover bg-center max-md:-mx-3 max-md:w-[calc(100%+1.5rem)] md:rounded-xl"
      style={poster ? { backgroundImage: `url("${poster}")` } : undefined}
    />
  );
}
